import * as React from "react";

import { ChevronDown, ChevronUp, Leaf } from "lucide-react";

import { ChecklistRow } from "~/components/ui/checklist-row";
import { FloatingHelperCard } from "~/components/ui/floating-helper-card";
import { FooterActionBar } from "~/components/ui/footer-action-bar";
import { RingMeter } from "~/components/ui/progress-ring";
import { cn } from "~/lib/utils";

import { ITEMS, type OnboardingItemId } from "../items";

export type OnboardingChecklistMode = "expanded" | "collapsed";

export interface OnboardingChecklistWidgetProps {
    completedIds: ReadonlySet<OnboardingItemId>;
    justDoneId?: OnboardingItemId | null;
    mode: OnboardingChecklistMode;
    onModeChange: (mode: OnboardingChecklistMode) => void;
    onItemClick: (id: OnboardingItemId) => void;
    /** Completed state's "Close onboarding" action. */
    onClose: () => void;
    /** FooterActionBar href; bar hidden if falsy. */
    callUrl?: string | null;
    onCallClick?: () => void;
    /** Narrow-viewport offsets. */
    narrow?: boolean;
    /** Defaults to fixed. Use false for showcase frames. */
    fixed?: boolean;
    /**
     * Extra right offset (px or CSS length) so the fixed widget clears a
     * right sidebar (e.g. the session consultant chat panel).
     */
    rightOffset?: number | string;
}

// AIDEV-NOTE: Ring/steps count ALL items (incl. the optional live call). But the
// optional item never *blocks* finishing: once every REQUIRED item is done a
// "Complete onboarding" button appears so the practitioner can finish without it.
// The full "You're all set" state only shows at a true 6/6.
const TOTAL = ITEMS.length;
const REQUIRED_ITEMS = ITEMS.filter((item) => !item.optional);
const FOOTER_LABEL = "Schedule a free 20m onboarding call";

function itemLabel(item: (typeof ITEMS)[number]) {
    return item.optional ? `${item.label} (Optional)` : item.label;
}

function stepsLeftLabel(stepsLeft: number) {
    return `${stepsLeft} to go`;
}

// AIDEV-NOTE: Footer CTA always renders; when ONBOARDING_CALL_URL isn't
// configured it shows inert/muted rather than hiding entirely.
function FooterCallBar({
    callUrl,
    onClick,
}: {
    callUrl?: string | null;
    onClick?: () => void;
}) {
    if (!callUrl) {
        return <FooterActionBar label={FOOTER_LABEL} disabled />;
    }

    return (
        <FooterActionBar
            label={FOOTER_LABEL}
            href={callUrl}
            target="_blank"
            rel="noreferrer"
            onClick={onClick}
        />
    );
}

export function OnboardingChecklistWidget({
    completedIds,
    justDoneId = null,
    mode,
    onModeChange,
    onItemClick,
    onClose,
    callUrl,
    onCallClick,
    narrow = false,
    fixed = true,
    rightOffset,
}: OnboardingChecklistWidgetProps) {
    // AIDEV-NOTE: All items are always shown; ones whose prerequisite isn't met
    // render locked (see item.requiresAny) rather than being hidden.
    const doneCount = ITEMS.filter((item) => completedIds.has(item.id)).length;
    const stepsLeft = TOTAL - doneCount;
    const allComplete = doneCount === TOTAL;
    // Every non-optional item done → the practitioner may finish now.
    const requiredComplete = REQUIRED_ITEMS.every((item) =>
        completedIds.has(item.id),
    );

    // AIDEV-NOTE: When a right sidebar is open (rightOffset), shift the fixed
    // widget left so it doesn't sit underneath the sidebar.
    const baseRight = narrow ? "12px" : "22px";
    const offsetCss =
        typeof rightOffset === "number" ? `${rightOffset}px` : rightOffset;
    const wrapperClassName = fixed
        ? cn("fixed z-40", narrow ? "bottom-[76px]" : "bottom-[22px]")
        : "relative z-0";
    const wrapperStyle: React.CSSProperties | undefined = fixed
        ? {
              right: offsetCss
                  ? `calc(${baseRight} + ${offsetCss})`
                  : baseRight,
          }
        : undefined;

    if (allComplete) {
        return (
            <div className={wrapperClassName} style={wrapperStyle}>
                <FloatingHelperCard width={370} className="animate-oc-rise">
                    <div className="px-5 pb-[18px] pt-[22px] text-left">
                        <div className="mb-3.5 flex items-center gap-3">
                            <div className="flex size-11 shrink-0 animate-oc-pop items-center justify-center rounded-full bg-brand-forest-800 text-white">
                                <Leaf className="size-[22px] stroke-2" />
                            </div>
                            <div>
                                <div className="font-display text-[19px] font-semibold leading-[1.2] tracking-[-0.2px] text-brand-forest-800">
                                    You're all set
                                </div>
                                <div className="mt-0.5 font-body text-xs font-medium uppercase tracking-[0.2px] text-brand-forest-500">
                                    {doneCount} of {TOTAL} complete
                                </div>
                            </div>
                        </div>
                        <p className="mb-4 mt-0 font-body text-[13.5px] leading-[1.6] text-brand-forest-700">
                            Thanks for setting things up. Parts Companion is
                            ready whenever you and your clients are — this
                            checklist won't show up again.
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full rounded-[10px] border border-brand-forest-300 bg-white px-3.5 py-[11px] font-body text-[13px] font-semibold tracking-[-0.1px] text-brand-forest-800 transition-colors duration-150 hover:bg-brand-forest-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-300"
                        >
                            Close onboarding
                        </button>
                    </div>
                    <FooterCallBar callUrl={callUrl} onClick={onCallClick} />
                </FloatingHelperCard>
            </div>
        );
    }

    if (mode === "collapsed") {
        return (
            <div className={wrapperClassName} style={wrapperStyle}>
                <FloatingHelperCard width={244}>
                    <button
                        type="button"
                        onClick={() => onModeChange("expanded")}
                        className="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-3.5 py-3 text-left font-body"
                    >
                        <RingMeter value={doneCount} max={TOTAL} size={38} />
                        <div className="min-w-0 flex-1">
                            <div className="font-body text-[13px] font-semibold tracking-[-0.1px] text-brand-forest-800">
                                First steps
                            </div>
                            <div className="font-body text-[11.5px] text-brand-forest-600">
                                {stepsLeftLabel(stepsLeft)}
                            </div>
                        </div>
                        <ChevronUp className="size-4 shrink-0 stroke-2 text-brand-forest-400" />
                    </button>
                </FloatingHelperCard>
            </div>
        );
    }

    return (
        <div className={wrapperClassName} style={wrapperStyle}>
            <FloatingHelperCard width={370}>
                <div className="border-b border-brand-forest-100 px-[15px] pb-3 pt-[15px]">
                    <div className="flex items-center gap-3">
                        <RingMeter value={doneCount} max={TOTAL} size={42} />
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-lg font-semibold leading-[1.2] tracking-[-0.2px] text-brand-forest-800">
                                First steps
                            </div>
                            <div className="mt-px font-body text-xs text-brand-forest-600">
                                {stepsLeftLabel(stepsLeft)}
                            </div>
                        </div>
                        <button
                            type="button"
                            title="Collapse"
                            aria-label="Collapse onboarding checklist"
                            onClick={() => onModeChange("collapsed")}
                            className="flex size-7 shrink-0 items-center justify-center self-start rounded-lg border border-brand-forest-200 bg-white text-brand-forest-600 transition-colors duration-150 hover:bg-brand-forest-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-300"
                        >
                            <ChevronDown className="size-4 stroke-2" />
                        </button>
                    </div>
                </div>

                <div className="px-1.5 py-[7px]">
                    {ITEMS.map((item) => {
                        const Icon = item.icon;
                        const locked =
                            item.requiresAny && item.requiresAny.length > 0
                                ? !item.requiresAny.some((id) =>
                                      completedIds.has(id),
                                  )
                                : false;
                        return (
                            <ChecklistRow
                                key={item.id}
                                label={itemLabel(item)}
                                icon={<Icon aria-hidden />}
                                done={completedIds.has(item.id)}
                                justDone={justDoneId === item.id}
                                locked={locked}
                                hint={locked ? item.lockedHint : undefined}
                                onClick={() => {
                                    if (!locked) onItemClick(item.id);
                                }}
                            />
                        );
                    })}
                </div>

                {/* AIDEV-NOTE: Once every required item is done, offer to finish —
                    the optional live call must never block completion. */}
                {requiredComplete ? (
                    <div className="px-3 pb-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full rounded-[10px] bg-brand-forest-800 px-3.5 py-[11px] font-body text-[13px] font-semibold tracking-[-0.1px] text-white transition-colors duration-150 hover:bg-brand-forest-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-300"
                        >
                            Complete onboarding
                        </button>
                    </div>
                ) : null}

                <div className="mt-1 border-t border-brand-forest-100">
                    <FooterCallBar callUrl={callUrl} onClick={onCallClick} />
                </div>
            </FloatingHelperCard>
        </div>
    );
}
